import { ArchiveCreativeEntity } from "./ArchiveCreativeEntity";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader2 } from "@digital-twins/design-system/icons";
import type { CreativeMaterialRole } from "@digital-twins/shared-types";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Text } from "@/components/ui/Text";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MaterialHelpDialog } from "@/components/creative-materials/MaterialHelpDialog";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import { PrivateReferenceRow } from "./PrivateReferenceRow";
import { UnavailableReferenceRow } from "./UnavailableReferenceRow";
import { useAttachedReferences } from "./useAttachedReferences";
import { CREATIVE_REFERENCE_LIMIT } from "@digital-twins/shared-types";
import type { BoardEditorProps } from "@/types/creative-library";

const ROLES: CreativeMaterialRole[] = [
  "product",
  "logo",
  "style",
  "composition",
  "identity",
];
export function BoardEditor({
  open,
  board,
  brands,
  creatives,
  onClose,
  onSaved,
}: BoardEditorProps) {
  const { t } = useTranslation("app");
  const [name, setName] = useState("");
  const [brandToken, setBrandToken] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [channels, setChannels] = useState("");
  const [size, setSize] = useState<"1024x1024" | "1536x1024" | "1024x1536">(
    "1024x1024",
  );
  const [references, setReferences] = useState<string[]>([]);
  const [required, setRequired] = useState<CreativeMaterialRole[]>(["product"]);
  const [applicable, setApplicable] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const attached = useAttachedReferences(
    open,
    references,
    creatives,
    board?.hiddenReferenceTokens,
  );
  const referenceChoices = [...attached.found, ...creatives];
  useEffect(() => {
    if (open) {
      setName(board?.name ?? "");
      setBrandToken(
        board?.brandToken ?? brands.find((brand) => brand.canEdit)?.token ?? "",
      );
      setDescription(board?.description ?? "");
      setInstructions(board?.definition.instructions ?? "");
      setChannels(board?.channelTags.join(", ") ?? "");
      setSize(board?.definition.defaultSize ?? "1024x1024");
      setReferences(board?.definition.referenceTokens ?? []);
      setRequired(
        board?.definition.slots
          .filter((slot) => slot.required)
          .map((slot) => slot.role) ?? ["product"],
      );
      setApplicable(board?.applicableBrandTokens ?? []);
      setError("");
    }
  }, [open, board, brands]);
  async function save() {
    setSaving(true);
    setError("");
    const body = {
      brandToken,
      name: name.trim(),
      description,
      channelTags: channels
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      applicableBrandTokens: Array.from(new Set([brandToken, ...applicable])),
      definition: {
        instructions,
        slots: ROLES.map((role) => ({
          role,
          required: required.includes(role),
        })),
        referenceTokens: references,
        defaultSize: size,
      },
    };
    try {
      const { brandToken: _brand, ...patch } = body;
      const saved = board
        ? await creativeLibraryApi.updateBoard(board.token, {
            ...patch,
            revision: board.revision,
          })
        : await creativeLibraryApi.createBoard(body);
      onSaved(saved);
      onClose();
    } catch {
      setError(t("creativeLibrary.saveFailed"));
    } finally {
      setSaving(false);
    }
  }
  function toggle(values: string[], value: string, checked: boolean) {
    return checked
      ? [...values, value]
      : values.filter((item) => item !== value);
  }
  return (
    <CreativeEditorDialog
      open={open}
      busy={saving}
      onClose={onClose}
      kind="boards"
      title={t(
        board ? "creativeLibrary.editBoard" : "creativeLibrary.newBoard",
      )}
      description={t("creativeLibrary.boardEditorHint")}
      testId="creative-board-editor"
      footer={
        <>
          {board?.canEdit && (
            <ArchiveCreativeEntity
              kind="boards"
              token={board.token}
              disabled={saving}
              onArchived={() => {
                onSaved();
                onClose();
              }}
            />
          )}
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="creative-board-cancel"
          >
            {t("creativeLibrary.cancel")}
          </Button>
          <Button
            variant="primary"
            className="gap-2"
            onClick={() => void save()}
            disabled={
              !name.trim() || !brandToken || !instructions.trim() || saving
            }
            data-testid="creative-board-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("creativeLibrary.saveBoard")}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="board-name">{t("creativeLibrary.name")}</Label>
          <Input
            autoFocus
            id="board-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="creative-board-name"
            maxLength={255}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t("creativeLibrary.brand")}</Label>
          <Select
            value={brandToken}
            onValueChange={setBrandToken}
            disabled={Boolean(board)}
          >
            <SelectTrigger
              aria-label={t("creativeLibrary.brand")}
              data-testid="creative-board-brand"
            >
              <SelectValue placeholder={t("creativeLibrary.chooseBrand")} />
            </SelectTrigger>
            <SelectContent>
              {brands
                .filter((b) => b.canEdit)
                .map((b) => (
                  <SelectItem key={b.token} value={b.token}>
                    {b.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="board-description">
          {t("creativeLibrary.purpose")}
        </Label>
        <Input
          id="board-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="creative-board-description"
        />
      </div>
      <CreativeEditorSection>
        <div className="flex items-center justify-between">
          <Label htmlFor="board-instructions">
            {t("creativeLibrary.creativeDirection")}
          </Label>
          <MaterialHelpDialog kind="board" />
        </div>
        <Textarea
          id="board-instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          maxLength={4000}
          placeholder={t("creativeLibrary.directionPlaceholder")}
          data-testid="creative-board-instructions"
        />
      </CreativeEditorSection>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="board-channels">
            {t("creativeLibrary.channelTags")}
          </Label>
          <Input
            id="board-channels"
            value={channels}
            onChange={(e) => setChannels(e.target.value)}
            placeholder={t("creativeLibrary.channelsPlaceholder")}
            data-testid="creative-board-channels"
          />
          <Text size="xs" color="muted">
            {t("creativeLibrary.channelHint")}
          </Text>
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t("creativeLibrary.defaultFormat")}</Label>
          <Select
            value={size}
            onValueChange={(value) => setSize(value as typeof size)}
          >
            <SelectTrigger
              aria-label={t("creativeLibrary.defaultFormat")}
              data-testid="creative-board-size"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1024x1024">
                {t("creativeLibrary.square")}
              </SelectItem>
              <SelectItem value="1536x1024">
                {t("creativeLibrary.landscape")}
              </SelectItem>
              <SelectItem value="1024x1536">
                {t("creativeLibrary.portrait")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <CreativeEditorSection>
        <Label>{t("creativeLibrary.requiredInputs")}</Label>
        <div className="flex flex-wrap gap-4">
          {ROLES.map((role) => (
            <label
              key={role}
              className="flex min-h-11 items-center gap-2 text-sm"
            >
              <Checkbox
                checked={required.includes(role)}
                onCheckedChange={(checked) =>
                  setRequired(
                    toggle(
                      required,
                      role,
                      checked === true,
                    ) as CreativeMaterialRole[],
                  )
                }
                data-testid={`creative-board-required-${role}`}
              />
              {t(`creativeLibrary.roles.${role}`)}
            </label>
          ))}
        </div>
      </CreativeEditorSection>
      {(referenceChoices.length > 0 ||
        attached.unavailable.length > 0 ||
        attached.hidden.length > 0) && (
        <div className="flex flex-col gap-3">
          <Label>{t("creativeLibrary.savedReferences")}</Label>
          <Text size="xs" color="muted">
            {t("creativeLibrary.boardStaticReferencesHint")}
          </Text>
          <div className="max-h-36 overflow-y-auto rounded-lg border border-border p-3">
            {attached.unavailable.map((token) => (
              <UnavailableReferenceRow
                key={token}
                token={token}
                onRemove={() =>
                  setReferences(toggle(references, token, false))
                }
              />
            ))}
            {attached.hidden.map((token) => (
              <PrivateReferenceRow key={token} token={token} />
            ))}
            {referenceChoices.map((item) => (
              <label
                key={item.token}
                className="flex min-h-11 items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={references.includes(item.token)}
                  disabled={
                    !references.includes(item.token) &&
                    references.length >= CREATIVE_REFERENCE_LIMIT
                  }
                  onCheckedChange={(checked) =>
                    setReferences(
                      toggle(references, item.token, checked === true),
                    )
                  }
                  data-testid={`creative-board-reference-${item.token}`}
                />
                <span className="truncate">{item.title}</span>
                <span className="text-muted-foreground">
                  {t(`creativeLibrary.roles.${item.role}`)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {brands.filter((b) => b.canEdit && b.token !== brandToken).length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>{t("creativeLibrary.alsoUseFor")}</Label>
          {brands
            .filter((b) => b.canEdit && b.token !== brandToken)
            .map((brand) => (
              <label
                key={brand.token}
                className="flex min-h-11 items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={applicable.includes(brand.token)}
                  onCheckedChange={(checked) =>
                    setApplicable(
                      toggle(applicable, brand.token, checked === true),
                    )
                  }
                />
                {brand.name}
              </label>
            ))}
        </div>
      )}
      {error && (
        <Text
          size="sm"
          className="flex items-center gap-2 text-destructive"
          role="alert"
        >
          <AlertCircle className="h-4 w-4" />
          {error}
        </Text>
      )}
    </CreativeEditorDialog>
  );
}
